import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import SEOHead from "@/components/SEOHead";
import {
  Users, Flame, AlarmClock, GraduationCap, Target, TrendingUp, Wallet,
  CalendarDays, ArrowRight, Loader2, ListChecks, AlertTriangle,
} from "lucide-react";
import { ADMISSION_STAGES, STAGE_COLORS, STATUS_COLORS, SOURCE_LABELS, stageOf } from "@/lib/crm";
import FunnelAnalytics from "@/components/admin/FunnelAnalytics";
import { V51Cutover } from "@/components/admin/V51Cutover";

export default function AdminDashboard() {
  const [biz, setBiz] = useState(null);
  const [recent, setRecent] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics/business").then((r) => setBiz(r.data)).catch(() => {});
    api.get("/admin/dashboard").then((r) => setRecent(r.data)).catch(() => {});
  }, []);

  if (!biz) return <div className="flex justify-center py-32"><Loader2 className="h-10 w-10 animate-spin text-[#0B2A6B]" /></div>;

  const kpis = [
    { label: "Today's Leads", value: biz.todayLeads, icon: CalendarDays, color: "#0B2A6B", to: "/admin/leads", action: "Review new leads" },
    { label: "This Week", value: biz.weekLeads, icon: TrendingUp, color: "#6366F1", to: "/admin/leads", action: "View pipeline" },
    { label: "This Month", value: biz.monthLeads, icon: Users, color: "#0EA5E9", to: "/admin/leads", action: "View pipeline" },
    { label: "Hot Leads", value: biz.hotLeads, icon: Flame, color: "#D4A24C", to: "/admin/workqueue", action: "Work hot leads now" },
    { label: "Overdue Follow-ups", value: biz.overdueFollowUps, icon: AlarmClock, color: "#EF4444", to: "/admin/workqueue", danger: biz.overdueFollowUps > 0, action: biz.overdueFollowUps > 0 ? "Act now — overdue" : "All on track" },
    { label: "Admissions Won", value: biz.admissions, icon: GraduationCap, color: "#10B981", to: "/admin/leads", action: "See won deals" },
    { label: "Conversion Rate", value: `${biz.conversionRate}%`, icon: Target, color: "#8B5CF6", to: "/admin/analytics", action: "Analyse funnel" },
    { label: "Total Leads", value: biz.totalLeads, icon: Users, color: "#0B2A6B", to: "/admin/leads", action: "Open all leads" },
  ];

  const maxStage = Math.max(1, ...biz.stageDistribution.map((s) => s.count));
  const needsAttention = biz.overdueFollowUps > 0 || biz.hotLeads > 0;

  return (
    <div data-testid="admin-dashboard-page" className="space-y-8">
      <SEOHead title="Founder Dashboard" />

      <V51Cutover />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Founder Dashboard</h1>
          <p className="text-sm text-slate-500">Business health & admission pipeline at a glance</p>
        </div>
        <Link to="/admin/workqueue" data-testid="dash-workqueue-link" className="inline-flex items-center gap-2 rounded-lg bg-[#0B2A6B] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#081E4D]">
          <ListChecks className="h-4 w-4" /> Open Work Queue
        </Link>
      </div>

      {/* Action banner */}
      {needsAttention && (
        <Link
          to="/admin/workqueue"
          data-testid="dash-attention-banner"
          className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 transition-colors hover:bg-amber-100"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100"><AlertTriangle className="h-5 w-5 text-amber-600" /></span>
            <div>
              <p className="text-sm font-semibold text-slate-900">Action needed in your pipeline</p>
              <p className="text-sm text-slate-600">
                {biz.overdueFollowUps > 0 && <><span className="font-semibold text-red-600">{biz.overdueFollowUps} overdue follow-up{biz.overdueFollowUps === 1 ? "" : "s"}</span>{biz.hotLeads > 0 ? " · " : ""}</>}
                {biz.hotLeads > 0 && <><span className="font-semibold text-[#C08E3B]">{biz.hotLeads} hot lead{biz.hotLeads === 1 ? "" : "s"}</span> awaiting your team</>}
              </p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#0B2A6B]">Go to Work Queue <ArrowRight className="h-4 w-4" /></span>
        </Link>
      )}

      {/* Business KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <Link
            key={k.label}
            to={k.to}
            data-testid={`kpi-${k.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
            className={`group flex h-full flex-col rounded-2xl border bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${k.danger ? "border-red-200" : "border-slate-200"}`}
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: `${k.color}1a` }}>
              <k.icon className="h-5 w-5" style={{ color: k.color }} />
            </span>
            <p className={`mt-3 font-display text-3xl font-extrabold ${k.danger ? "text-red-600" : "text-slate-900"}`}>{k.value}</p>
            <p className="text-sm text-slate-500">{k.label}</p>
            <span className={`mt-2 inline-flex items-center gap-1 text-xs font-semibold ${k.danger ? "text-red-600" : "text-[#0B2A6B]"} opacity-70 transition-opacity group-hover:opacity-100`}>
              {k.action} <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
            </span>
          </Link>
        ))}
      </div>

      {/* Revenue */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div data-testid="dash-counselling-revenue" className="rounded-2xl border border-[#D4A24C]/40 bg-gradient-to-br from-[#D4A24C] to-[#C08E3B] p-6 text-white shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-white/90 text-sm"><Wallet className="h-4 w-4" /> Counselling Revenue (Collected)</div>
            <span data-testid="dash-payment-mode" className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">{biz.paymentMode === "mock" ? "Demo" : "Live"}</span>
          </div>
          <p className="mt-2 font-display text-3xl font-extrabold">₹{formatINR(biz.counsellingRevenue || 0)}</p>
          <p className="text-xs text-white/80">{biz.paidBookings || 0} paid booking{biz.paidBookings === 1 ? "" : "s"}{biz.pendingBookings ? ` · ${biz.pendingBookings} pending` : ""}{biz.counsellingRevenueToday ? ` · ₹${formatINR(biz.counsellingRevenueToday)} today` : ""}</p>
        </div>
        <div data-testid="dash-revenue-projection" className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#0B2A6B] to-[#102f78] p-6 text-white shadow-sm">
          <div className="flex items-center gap-2 text-white/80 text-sm"><Wallet className="h-4 w-4" /> Pipeline Revenue Projection</div>
          <p className="mt-2 font-display text-3xl font-extrabold">₹{formatINR(biz.revenueProjection)}</p>
          <p className="text-xs text-white/60">Weighted by stage probability across open leads</p>
        </div>
        <div data-testid="dash-revenue-won" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 shadow-sm">
          <div className="flex items-center gap-2 text-emerald-700 text-sm"><GraduationCap className="h-4 w-4" /> Confirmed Admission Revenue</div>
          <p className="mt-2 font-display text-3xl font-extrabold text-emerald-700">₹{formatINR(biz.revenueWon)}</p>
          <p className="text-xs text-emerald-600/70">From {biz.admissions} confirmed admission{biz.admissions === 1 ? "" : "s"}</p>
        </div>
      </div>

      {/* Booking breakdown by plan */}
      {biz.bookingsByPlan && biz.bookingsByPlan.length > 0 && (
        <div data-testid="dash-bookings-by-plan" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-3 font-display font-bold text-slate-900 text-sm">Expert Counselling Bookings by Plan</h2>
          <div className="flex flex-wrap gap-3">
            {biz.bookingsByPlan.map((p) => (
              <span key={p.name} className="inline-flex items-center gap-2 rounded-lg bg-[#0B2A6B]/5 px-3 py-1.5 text-sm text-slate-700 ring-1 ring-[#0B2A6B]/10">
                {p.name} <span className="rounded-md bg-[#0B2A6B] px-2 py-0.5 text-xs font-bold text-white">{p.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Pipeline + Recent */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="dash-pipeline">
          <h2 className="mb-4 font-display font-bold text-slate-900">Admission Pipeline</h2>
          <div className="space-y-3">
            {biz.stageDistribution.map((s) => (
              <div key={s.stage}>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{s.stage}</span>
                  <span className="font-semibold text-slate-900">{s.count}</span>
                </div>
                <div className="mt-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full bg-[#0B2A6B]" style={{ width: `${(s.count / maxStage) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="dash-recent">
          <div className="flex items-center justify-between p-5 border-b border-slate-100">
            <h2 className="font-display font-bold text-slate-900">Recent Leads</h2>
            <Link to="/admin/leads" className="inline-flex items-center gap-1 text-sm font-semibold text-[#0B2A6B] hover:gap-2 transition-all">All <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="divide-y divide-slate-100">
            {!recent ? (
              <div className="p-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-300" /></div>
            ) : recent.recentLeads.length === 0 ? (
              <p className="p-6 text-sm text-slate-400">No leads yet.</p>
            ) : recent.recentLeads.map((l) => (
              <Link key={l.id} to={`/admin/leads/${l.id}`} className="flex items-center justify-between gap-2 p-4 hover:bg-slate-50" data-testid={`recent-lead-${l.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-sm font-bold text-slate-600 shrink-0">{(l.name || "?")[0]?.toUpperCase()}</span>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 truncate">{l.name}</p>
                    <p className="text-xs text-slate-500 truncate">{SOURCE_LABELS[l.source] || l.source} · {l.mobile}</p>
                  </div>
                </div>
                <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${STAGE_COLORS[stageOf(l)] || STATUS_COLORS[l.status]}`}>{stageOf(l)}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Admissions breakdowns — only when admissions exist */}
      {biz.admissions > 0 && (
        <div>
          <h2 className="mb-4 font-display text-lg font-bold text-slate-900">Where admissions are coming from</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Breakdown title="By Counsellor" rows={biz.admissionsByCounsellor} testid="dash-by-counsellor" />
            <Breakdown title="By Course" rows={biz.admissionsByCourse} testid="dash-by-course" />
            <Breakdown title="By College" rows={biz.admissionsByCollege} testid="dash-by-college" />
            <Breakdown title="By City" rows={biz.admissionsByCity} testid="dash-by-city" />
          </div>
        </div>
      )}

      <FunnelAnalytics />
    </div>
  );
}

function Breakdown({ title, rows, testid }) {
  const data = (rows || []).filter((r) => r.count > 0);
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="mb-3 font-display font-bold text-slate-900 text-sm">{title}</h3>
      {data.length === 0 ? (
        <p className="text-sm text-slate-400">No admissions yet.</p>
      ) : (
        <ul className="space-y-2">
          {data.map((r) => (
            <li key={r.name} className="flex items-center justify-between text-sm">
              <span className="truncate text-slate-600">{r.name}</span>
              <span className="ml-2 shrink-0 rounded-md bg-[#0B2A6B]/10 px-2 py-0.5 text-xs font-semibold text-[#0B2A6B]">{r.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
